import {
  Sidebar,
  SidebarDivider,
  SidebarGroup,
  SidebarItem,
  SidebarScrollWrapper,
  SidebarSpace,
} from '@backstage/core-components';
import { NavContentBlueprint } from '@backstage/plugin-app-react';
import { SidebarLogo } from './SidebarLogo';
import { NavSection, ExternalNavItem } from './NavSection';
import { useToolHealth, useAiTools } from './useToolHealth';
import MenuIcon from '@material-ui/icons/Menu';
import SearchIcon from '@material-ui/icons/Search';
import AccountTreeIcon from '@material-ui/icons/AccountTree';
import ChatIcon from '@material-ui/icons/Chat';
import BlurOnIcon from '@material-ui/icons/BlurOn';
import TimelineIcon from '@material-ui/icons/Timeline';
import AssignmentTurnedInIcon from '@material-ui/icons/AssignmentTurnedIn';
import LayersIcon from '@material-ui/icons/Layers';
import SyncAltIcon from '@material-ui/icons/SyncAlt';
import AccountBalanceWalletIcon from '@material-ui/icons/AccountBalanceWallet';
import PsychologyIcon from '@material-ui/icons/Bookmarks';
import PlayCircleOutlineIcon from '@material-ui/icons/PlayCircleOutline';
import HubIcon from '@material-ui/icons/DeviceHub';
import HealingIcon from '@material-ui/icons/Healing';
import MonetizationOnIcon from '@material-ui/icons/MonetizationOn';
import EqualizerIcon from '@material-ui/icons/Equalizer';
import { SidebarSearchModal } from '@backstage/plugin-search';
import { UserSettingsSignInAvatar } from '@backstage/plugin-user-settings';
import { NotificationsSidebarItem } from '@backstage/plugin-notifications';

const toolIcon = {
  n8n: <AccountTreeIcon fontSize="small" />,
  evolution: <ChatIcon fontSize="small" />,
  gateway: <BlurOnIcon fontSize="small" />,
  grafana: <TimelineIcon fontSize="small" />,
} as const;

// Split into its own component so the health hook has a stable render scope.
const Nav = ({ nav }: { nav: any }) => {
  const health = useToolHealth();
  const tools = useAiTools();

  const toolsIn = (group: 'workspace' | 'observability') =>
    tools
      .filter(t => t.group === group)
      .map(tool => (
        <ExternalNavItem
          key={tool.id}
          href={tool.url}
          text={tool.title}
          icon={toolIcon[tool.id as keyof typeof toolIcon]}
          status={health[tool.id]}
        />
      ));

  return (
    <Sidebar>
      <SidebarLogo />

      <SidebarGroup label="Search" icon={<SearchIcon />} to="/search">
        <SidebarSearchModal />
      </SidebarGroup>

      <SidebarGroup label="Menu" icon={<MenuIcon />}>
        {/* Sections collapse and remember their state. Passing the items as
            children puts them inside the panel that draws the emerald guide
            rail and lights up when the section holds the active page. */}
        <NavSection label="Platform">
          {nav.take('page:home')}
          {nav.take('page:catalog')}
          {nav.take('page:scaffolder')}
          {nav.take('page:techdocs')}
          {nav.take('page:api-docs')}
          {/* PageBlueprint alone does not produce a nav entry for this plugin,
              so the Assessments link is declared explicitly. */}
          <SidebarItem
            icon={AssignmentTurnedInIcon}
            to="/assessments"
            text="Assessments"
          />
        </NavSection>

        <NavSection label="Delivery">
          <SidebarItem icon={SyncAltIcon} to="/gitops" text="GitOps" />
          <SidebarItem
            icon={PlayCircleOutlineIcon}
            to="/pipelines"
            text="Pipelines"
          />
          <SidebarItem icon={LayersIcon} to="/crossplane" text="Crossplane" />
        </NavSection>

        <NavSection label="AI Workspace">
          <SidebarItem icon={HubIcon} to="/automation" text="Automation" />
          <SidebarItem icon={MonetizationOnIcon} to="/llmops" text="LLMOps" />
          {/* Key administration lives in the AI Gateway's own dashboard,
              linked below with the other workspace tools. */}
          <SidebarItem icon={PsychologyIcon} to="/memory" text="Memory" />
          {toolsIn('workspace')}
        </NavSection>

        <NavSection label="Observability">
          <SidebarItem icon={HealingIcon} to="/sre" text="SRE" />
          <SidebarItem
            icon={EqualizerIcon}
            to="/observability"
            text="Dashboards"
          />
          <SidebarItem
            icon={AccountBalanceWalletIcon}
            to="/finops"
            text="FinOps"
          />
          {toolsIn('observability')}
        </NavSection>

        <NavSection label="More" defaultOpen={false}>
          <SidebarScrollWrapper>
            {nav.rest({ sortBy: 'title' })}
          </SidebarScrollWrapper>
        </NavSection>
      </SidebarGroup>

      <SidebarSpace />
      <SidebarDivider />
      <NotificationsSidebarItem />
      <SidebarDivider />
      <SidebarGroup
        label="Settings"
        icon={<UserSettingsSignInAvatar />}
        to="/settings"
      >
        {nav.take('page:app-visualizer')}
        {nav.take('page:user-settings')}
      </SidebarGroup>
    </Sidebar>
  );
};

export const SidebarContent = NavContentBlueprint.make({
  params: {
    component: ({ navItems }) => {
      const nav = navItems.withComponent(item => (
        <SidebarItem icon={() => item.icon} to={item.href} text={item.title} />
      ));

      // Skipped items
      nav.take('page:search'); // Using search modal instead
      nav.take('page:notifications'); // Using NotificationsSidebarItem manually instead

      return <Nav nav={nav} />;
    },
  },
});
